import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import SEOPageLayout from '../components/seo/SEOPageLayout';

const relatedLinks = [
  { to: '/perte-information-entreprise', label: 'Ce que la perte d\'information coûte à votre entreprise' },
  { to: '/centraliser-information-entreprise', label: 'Pourquoi centraliser ne suffit pas' },
];

const RetrouperInformation = () => {
  return (
    <SEOPageLayout
      title="Pourquoi retrouver une information en entreprise prend autant de temps ?"
      metaTitle="Retrouver une information en entreprise | Cloneia"
      metaDescription="Chaque jour, vos équipes perdent des heures à chercher une information. Découvrez pourquoi et comment Cloneia change la donne."
      url="/retrouver-information-entreprise"
      readingTime={4}
      subtitle="Le problème n'est pas l'information. C'est l'accès."
      relatedLinks={relatedLinks}
    >
      <article className="space-y-12 text-[#a1a1aa] leading-relaxed" data-testid="seo-article-retrouver">

        {/* Section 1 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Le quotidien de chaque entreprise
          </h2>
          <p>
            Un client appelle. Vous devez retrouver l'historique de son dossier. Vous cherchez dans vos emails. Dans votre CRM. Dans vos messages. Vous finissez par appeler un collègue.
          </p>
          <p className="mt-4">
            Ce scénario se répète des dizaines de fois par jour dans votre entreprise. Et à chaque fois, c'est du temps perdu.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Le problème n'est pas le manque d'information. C'est qu'elle est dispersée partout.
          </p>
        </motion.div>

        {/* Section 2 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Où se cache l'information ?
          </h2>
          <p>
            Dans une entreprise moyenne, l'information est répartie dans 10 à 15 outils différents. Emails, messagerie instantanée, CRM, téléphone, notes de réunion, documents partagés...
          </p>
          <p className="mt-4">
            Chaque outil contient une partie de la réponse. Aucun ne contient la réponse complète. Et personne ne sait exactement où chercher.
          </p>
          <div className="mt-6 p-6 rounded-xl border border-white/5 bg-[#1a1a2e]/30">
            <p className="text-[#f5f5f5] font-medium mb-2">Un exemple concret</p>
            <p>
              Vous voulez savoir où en est le dossier Martin. L'information existe : un email de lundi, un appel de mardi, une note de mercredi. Mais pour reconstituer le tableau, il faut retrouver chaque morceau. Dans chaque outil. Auprès de chaque personne.
            </p>
          </div>
        </motion.div>

        {/* Section 3 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Le vrai coût de cette dispersion
          </h2>
          <p>
            Selon les études, un collaborateur passe en moyenne 2h30 par jour à chercher de l'information. C'est près d'un tiers de sa journée de travail.
          </p>
          <p className="mt-4">
            Multipliez ce chiffre par le nombre de collaborateurs dans votre entreprise. Le résultat est vertigineux.
          </p>
          <p className="mt-4">
            Mais le coût n'est pas seulement financier. C'est aussi de la frustration, des erreurs, des décisions prises sans avoir toutes les informations.
          </p>
        </motion.div>

        {/* Section 4 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Pourquoi les solutions actuelles ne fonctionnent pas
          </h2>
          <p>
            On vous a dit de tout centraliser. De créer des process. De remplir le CRM. De documenter.
          </p>
          <p className="mt-4">
            Mais la réalité est différente. Les gens oublient de saisir. Les process ne sont pas suivis. Et l'information continue de vivre dans les échanges quotidiens : appels, emails, messages.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Le problème n'est pas les outils. C'est qu'on demande aux humains de faire le travail de la machine.
          </p>
        </motion.div>

        {/* Section 5 - Cloneia */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Une nouvelle approche : poser la question
          </h2>
          <p>
            Imaginez un système qui capte automatiquement l'information de chaque échange. Sans saisie. Sans effort.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Cloneia crée un clone mémoire de chaque collaborateur. Toute l'information issue de ses échanges est captée et rendue accessible.
          </p>
          <p className="mt-4">
            Vous ne cherchez plus. Vous posez la question. Et vous obtenez la réponse en quelques secondes. Avec le contexte complet.
          </p>
        </motion.div>

        {/* Section 6 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Ce que ça change concrètement
          </h2>
          <div className="space-y-4">
            <div className="flex gap-4 items-start">
              <span className="text-[#3b82f6] font-bold text-lg mt-0.5">1.</span>
              <div>
                <p className="text-[#f5f5f5] font-medium">Vous retrouvez n'importe quelle information en quelques secondes</p>
                <p className="text-sm mt-1">Plus besoin de fouiller dans 10 outils différents.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <span className="text-[#3b82f6] font-bold text-lg mt-0.5">2.</span>
              <div>
                <p className="text-[#f5f5f5] font-medium">Vous ne dépendez plus des personnes</p>
                <p className="text-sm mt-1">L'information reste accessible même quand le collaborateur est absent.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <span className="text-[#3b82f6] font-bold text-lg mt-0.5">3.</span>
              <div>
                <p className="text-[#f5f5f5] font-medium">Vous avez toujours le contexte complet</p>
                <p className="text-sm mt-1">Pas un résumé. L'information complète, avec les détails.</p>
              </div>
            </div>
          </div>
        </motion.div>

      </article>
    </SEOPageLayout>
  );
};

export default RetrouperInformation;
